import { cn } from "@design-system/lib/utils";
import { trans, plainTrans } from "@mongez/localization";
import { BookMarkedIcon, ChevronDownIcon } from "lucide-react";
import type {
  GradeDetailsCategoryViewModel,
  GradeDetailsTheme,
} from "../../hooks/use-grade-details-page.hook";

type GradeChapterNavigatorProps = {
  categories: GradeDetailsCategoryViewModel[];
  gradeName: string;
  theme: GradeDetailsTheme;
};

export function GradeChapterNavigator({
  categories,
  gradeName,
  theme,
}: GradeChapterNavigatorProps) {
  return (
    <nav
      aria-label={plainTrans("grades.gradeCategories")}
      className="rounded-[1.75rem] border border-slate-200/80 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900 sm:p-5"
    >
      <div className="mb-4 flex items-center gap-3">
        <span
          className={cn(
            "flex size-10 items-center justify-center rounded-2xl bg-gradient-to-br text-white",
            theme.gradient,
          )}
        >
          <BookMarkedIcon className="size-5" />
        </span>
        <div>
          <h2 className="font-heading text-base font-black text-slate-950 dark:text-white sm:text-lg">
            {trans("grades.gradeCategoriesList", {
              grade: gradeName,
            })}
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 sm:text-sm">
            {trans("grades.categoriesDescription")}
          </p>
        </div>
      </div>

      <div className="flex snap-x gap-3 overflow-x-auto pb-2">
        {categories.map(item => (
          <a
            key={item.category.id}
            href={`#${item.sectionId}`}
            className="group flex min-w-[210px] snap-start items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 transition hover:-translate-y-0.5 hover:border-primary-300 hover:bg-white hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-primary-600 dark:hover:bg-slate-800"
          >
            <span
              className={cn(
                "flex size-10 shrink-0 items-center justify-center rounded-xl text-sm font-black",
                theme.soft,
              )}
            >
              {item.index}
            </span>
            <span className="min-w-0 flex-1">
              <strong className="block truncate text-sm text-slate-900 dark:text-white">
                {item.category.name}
              </strong>
              <small className="text-xs text-slate-500 dark:text-slate-400">
                {trans("grades.totalLessons", { count: item.lessonCount })}
              </small>
            </span>
            <ChevronDownIcon
              className={cn(
                "size-4 shrink-0 transition-transform group-hover:translate-y-0.5",
                theme.accent,
              )}
            />
          </a>
        ))}
      </div>
    </nav>
  );
}
